/**
 * Utility functions for handling URL encoding and construction
 */

/**
 * Properly encodes a file URL, ensuring that filenames with special characters 
 * are correctly encoded while preserving the path structure.
 * 
 * @param url - The URL or file path to encode
 * @returns The properly encoded URL
 */
export const encodeFileUrl = (url: string): string => {
  if (!url) return url;

  // If the URL contains a filename that needs encoding
  if (url.includes('/')) {
    const urlParts = url.split('/');
    const filename = urlParts[urlParts.length - 1];

    // Better check: only skip encoding if filename looks already URL-encoded
    // (contains % followed by two hex digits)
    const alreadyEncoded = /%[0-9A-Fa-f]{2}/.test(filename);

    if (!alreadyEncoded) {
      // Encode the filename part with additional encoding for parentheses
      let encodedFilename = encodeURIComponent(filename);
      // Ensure parentheses are properly encoded
      encodedFilename = encodedFilename.replace(/\(/g, '%28').replace(/\)/g, '%29');
      urlParts[urlParts.length - 1] = encodedFilename;
    }
    return urlParts.join('/');
  } else {
    // If it's just a filename, encode the whole thing if not already encoded
    const alreadyEncoded = /%[0-9A-Fa-f]{2}/.test(url);

    if (!alreadyEncoded) {
      let encoded = encodeURIComponent(url);
      // Ensure parentheses are properly encoded
      encoded = encoded.replace(/\(/g, '%28').replace(/\)/g, '%29');
      return encoded;
    } else {
      return url;
    }
  }
};

/**
 * Constructs a complete download URL with proper encoding
 * 
 * @param baseUrl - The base URL (e.g., API base URL)
 * @param filePath - The file path or URL from the API
 * @returns A properly constructed and encoded download URL
 */
export const constructDownloadUrl = (baseUrl: string, filePath: string): string => {
  if (!filePath) return '';

  // If the file path is already a complete URL, just encode the filename
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return encodeFileUrl(filePath);
  }

  // Encode the file path
  const encodedPath = encodeFileUrl(filePath);

  // Check if baseUrl ends with '/' and if encodedPath starts with '/' to avoid double slashes
  // and ensure proper URL construction
  const baseEndsWithSlash = baseUrl.endsWith('/');
  const pathStartsWithSlash = encodedPath.startsWith('/');

  let separator = '';
  if (!baseEndsWithSlash && !pathStartsWithSlash) {
    separator = '/';
  } else if (baseEndsWithSlash && pathStartsWithSlash) {
    // Remove leading slash from path to avoid double slashes
    return `${baseUrl}${encodedPath.substring(1)}`;
  }

  return `${baseUrl}${separator}${encodedPath}`;
};

/**
 * Downloads a file by opening it in a new tab with proper URL encoding
 * 
 * @param baseUrl - The base URL
 * @param filePath - The file path from the API
 * @param cleanFileName - Optional clean filename for download (removes timestamp prefix)
 */
export const downloadFile = (baseUrl: string, filePath: string, cleanFileName?: string): void => {
  const downloadUrl = constructDownloadUrl(baseUrl, filePath);

  if (cleanFileName) {
    // Use download attribute to set clean filename
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = cleanFileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    // Fallback to opening in new tab
    window.open(downloadUrl, '_blank');
  }
};
